import {
	PLANS_PAGE_PRODUCTS,
	Product
} from '@brand-lolly/app/billing/types/product.types';
import { ProductAlias } from '@brand-lolly/app/billing/types/billing.enums';

/**
 * The product tab strip shared by the two pages that sell plans — the public
 * pricing page and the settings plans page. Framework-free, so both build the
 * same tabs in the same order and open on the same product.
 *
 * WHY THIS IS SHARED RATHER THAN COPIED. Both pages had the same defect and it
 * was fixed once on one of them, which left the bug live on the other: the two
 * screens are the same offer, and a visitor who lands on one and then the other
 * must not see the products in a different order or a different tab preselected.
 * The pages still own everything around it — their own `FormControl`, their own
 * "what does the active product show" — this owns only which tabs there are,
 * in what order, and which one opens.
 */
export interface ProductTabOption {
	title: string;
	productId: number;
	/** What the tab IS, independent of the id the environment gave it — the
	 *  default is picked by this, never by position. */
	alias?: ProductAlias;
}

/**
 * The tabs: the products these pages sell (see {@link PLANS_PAGE_PRODUCTS}), in
 * THE PAGES' OWN ORDER rather than in the order `GET /billing/catalog/products`
 * happens to answer.
 *
 * That endpoint answers in catalogue row order, and on dev that is
 * `creator_data_api` (id 4) before `application` (id 6) — so both pages opened
 * on the Creator Data API's tiers and the platform, the thing they exist to
 * sell, was a click away. Which products appear is already a fact about these
 * pages (that is what the allow-list is), so the order they appear in is one
 * too; taking it from the catalogue made the page's shape depend on how an
 * environment happened to be seeded.
 *
 * FALLS BACK TO EVERY ACTIVE PRODUCT when the allow-list matches none of them —
 * an environment whose catalogue predates the `application` product, or one
 * where `/catalog/products` omits the alias. An unrecognised catalogue must
 * degrade to the old page rather than to an empty one; those products are all
 * ranked equal and the sort is stable, so they keep the catalogue order they
 * arrived in.
 */
export function buildProductTabs(products: Product[]): ProductTabOption[] {
	const active = products.filter(product => product.is_active);
	const sellable = active.filter(
		product =>
			product.alias !== undefined &&
			PLANS_PAGE_PRODUCTS.includes(product.alias)
	);
	const distinct = new Map<number, ProductTabOption>();
	for (const product of sellable.length > 0 ? sellable : active) {
		if (distinct.has(product.id)) continue;
		distinct.set(product.id, {
			title: product.title,
			productId: product.id,
			alias: product.alias
		});
	}
	return Array.from(distinct.values()).sort(
		(a, b) => tabRank(a) - tabRank(b)
	);
}

/**
 * Which tab opens: the one the link asked for, else the platform, else the
 * first.
 *
 * The platform is resolved BY ALIAS. An index would have been a coincidence
 * that held on one machine — product ids are database-assigned and differ
 * between environments — and it is exactly the coincidence that put these pages
 * on the wrong product. `requestedProductId` keeps its precedence so a link
 * aimed at one product's plans still lands on it.
 */
export function defaultProductTab(
	tabs: ProductTabOption[],
	requestedProductId: number | null
): ProductTabOption | null {
	const requested =
		requestedProductId === null
			? undefined
			: tabs.find(tab => tab.productId === requestedProductId);
	return (
		requested ??
		tabs.find(tab => tab.alias === ProductAlias.APPLICATION) ??
		tabs[0] ??
		null
	);
}

/** Where a tab sits, by the pages' own list; anything it does not name sorts
 *  after everything it does. */
function tabRank(tab: ProductTabOption): number {
	const rank =
		tab.alias === undefined ? -1 : PLANS_PAGE_PRODUCTS.indexOf(tab.alias);
	return rank === -1 ? Number.MAX_SAFE_INTEGER : rank;
}
